import { useState } from 'react';
import { User, Briefcase, GraduationCap, ArrowRight, FileText, Mic, Calculator, TrendingUp, Crown, Brain, MessageSquare, Scale, Shield, Users, Rocket } from 'lucide-react';
import ServicesModal from './ServicesModal';
import ProfessionalsModal from './ProfessionalsModal';
import FormationsModal from './FormationsModal';
import { LucideIcon } from 'lucide-react';
import particuliersBg from '../assets/fond_particuliers.jpg';
import formationsBg from '../assets/fond_formations.jpg';
import professionalsBg from '../assets/fond-pro-2moy.jpg';

interface Feature {
  text: string;
  icon: LucideIcon;
}

interface ServiceCategory {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  features: Feature[];
  bgImage: string;
}

export default function Services() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProfessionalsModalOpen, setIsProfessionalsModalOpen] = useState(false);
  const [isFormationsModalOpen, setIsFormationsModalOpen] = useState(false);

  const categories: ServiceCategory[] = [
    {
      id: 'particuliers',
      title: 'Particuliers',
      icon: <User className="w-12 h-12" />,
      description: 'Accompagnement personnalisé pour vos projets professionnels',
      features: [
        { text: 'Optimisation du CV et lettre de motivation', icon: FileText },
        { text: 'Formation à la prise de parole', icon: Mic },
        { text: 'Aide à la compréhension du bulletin de salaire', icon: Calculator },
        { text: 'Coaching pour négociations salariales', icon: TrendingUp }
      ],
      bgImage: particuliersBg
    },
    {
      id: 'formations',
      title: 'Formations',
      icon: <GraduationCap className="w-12 h-12" />,
      description: 'Formation professionnelle adaptée à vos besoins',
      features: [
        { text: 'Management et leadership', icon: Crown },
        { text: 'Gestion du stress et prévention RPS', icon: Brain },
        { text: 'Communication et relations de travail', icon: MessageSquare },
        { text: 'Droit du travail pour managers', icon: Scale }
      ],
      bgImage: formationsBg
    },
    {
      id: 'professionnels',
      title: 'Professionnels',
      icon: <Briefcase className="w-12 h-12" />,
      description: 'Solutions RH complètes pour les entreprises et collectivités',
      features: [
        { text: 'Audit et conformité RH', icon: Shield },
        { text: 'Accompagnement en gestion des conflits', icon: Users },
        { text: 'Formation des managers', icon: Crown },
        { text: 'Accompagnement à la création d\'entreprise', icon: Rocket }
      ],
      bgImage: professionalsBg
    }
  ];

  const handleNavigation = (id: string) => {
    if (id === 'particuliers') {
      setIsModalOpen(true);
    } else if (id === 'professionnels') {
      setIsProfessionalsModalOpen(true);
    } else if (id === 'formations') {
      setIsFormationsModalOpen(true);
    }
  };

  return (
    <section id="services" className="py-24 bg-warm-gray-50 dark:bg-warm-gray-900">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-5 py-2.5 bg-aurore-light/50 dark:bg-warm-gray-800 rounded-full">
              <span className="text-aurore-dark dark:text-aurore-light text-sm font-medium">Accompagnement RH & droit social</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-8 leading-tight">
              Que vous soyez salarié, dirigeant ou manager : je vous aide concrètement
            </h2>

            <p className="text-xl text-warm-gray-700 dark:text-warm-gray-300 max-w-3xl mx-auto leading-relaxed-plus">
              Recrutement, conflit, audit RH, formation… Je m'adapte à votre situation réelle. Pas de forfait standard, juste une réponse à votre besoin.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleNavigation(category.id)}
                className="group relative cursor-pointer bg-white dark:bg-warm-gray-800 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-500 p-10 border border-aurore-light/30 dark:border-warm-gray-700 hover:border-aurore-medium text-left w-full flex flex-col overflow-hidden"
                aria-label={`En savoir plus sur ${category.title}`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                  style={{
                    backgroundImage: `url(${category.bgImage})`,
                  }}
                />
                <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-70 transition-opacity duration-700" />

                <div className="relative z-10">
                  <div className="w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-7 transition-all duration-500 bg-aurore-light/40 dark:bg-warm-gray-700 text-aurore-dark dark:text-aurore-light group-hover:bg-white group-hover:text-aurore-dark group-hover:scale-110">
                    {category.icon}
                  </div>

                  <h3 className="text-2xl font-bold mb-5 text-center transition-colors text-warm-gray-900 dark:text-warm-gray-100 group-hover:text-white">
                    {category.title}
                  </h3>

                  <p className="mb-8 text-center leading-relaxed-plus text-warm-gray-700 dark:text-warm-gray-300 group-hover:text-white/95">
                    {category.description}
                  </p>

                  <ul className="space-y-4 mb-8 flex-grow">
                    {category.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <feature.icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-aurore-dark dark:text-aurore-light group-hover:text-white" />
                        <span className="text-sm leading-relaxed text-warm-gray-700 dark:text-warm-gray-300 group-hover:text-white/95">{feature.text}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center justify-center space-x-2 font-medium text-aurore-dark dark:text-aurore-light group-hover:text-white">
                    <span>Découvrir</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-500" />
                  </div>
                </div>
              </button>
            ))}
          </div>

          <a
            href="https://baobab-ff.com/formations/"
            target="_blank"
            rel="noopener"
            className="group mt-16 block bg-white dark:bg-warm-gray-800 rounded-2xl border border-aurore-light/30 dark:border-warm-gray-700 shadow-sm hover:shadow-lg transition-all duration-500 p-6 md:p-8"
          >
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
              <img
                src="/qualiopi.png"
                alt="Logo Qualiopi"
                className="h-16 md:h-20 w-auto object-contain flex-shrink-0"
                loading="lazy"
              />
              <div className="text-center md:text-left">
                <p className="text-warm-gray-800 dark:text-warm-gray-100 leading-relaxed">
                  Aurore RH vous accompagne avec la garantie Qualiopi,
                  <span className="block md:inline"> portée par </span>
                  <span className="font-semibold text-aurore-dark dark:text-aurore-light">Baobab Formation Financeur</span>.
                </p>
              </div>
              <img
                src="/baobab-ff.jpg"
                alt="Logo Baobab Formation Financeur"
                className="h-16 md:h-20 w-auto object-contain flex-shrink-0"
                loading="lazy"
              />
            </div>
          </a>

          <div className="mt-20 bg-gradient-to-br from-aurore-dark/90 via-aurore-medium/90 to-aurore-light dark:from-warm-gray-800 dark:via-warm-gray-800 dark:to-warm-gray-700 rounded-3xl p-12 md:p-16 text-white text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">Votre situation ne rentre pas dans une case ?</h3>
            <p className="text-xl mb-10 text-white/95 dark:text-warm-gray-300 leading-relaxed-plus max-w-2xl mx-auto">
              Parfait. La plupart des situations humaines sont uniques. Appelons-nous ou prenons rendez-vous, je vous dirai tout de suite si je peux vous aider.
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  const offset = 80;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }
              }}
              className="bg-white dark:bg-aurore-dark text-aurore-dark dark:text-white px-10 py-5 rounded-2xl font-medium hover:bg-aurore-pale dark:hover:bg-aurore-medium hover:shadow-lg transition-all duration-500 inline-flex items-center space-x-3 shadow-md"
            >
              <span>Parlons-en maintenant</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <ServicesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <ProfessionalsModal
        isOpen={isProfessionalsModalOpen}
        onClose={() => setIsProfessionalsModalOpen(false)}
      />

      <FormationsModal
        isOpen={isFormationsModalOpen}
        onClose={() => setIsFormationsModalOpen(false)}
      />
    </section>
  );
}
